/**
 * Presenter de visita (LAIS) — porta a lógica de
 * `utils/visits/getVisitCardStatus.js` (app do corretor QuintoAndar) para o
 * vocabulário PT-BR de `Visita::STATUS`.
 *
 * Status persistidos (backend): solicitada | agendada | confirmada | paralisada |
 * realizada | cancelada | nao_compareceu | nao_sucedida.
 *
 * Card status derivados (UI), espelhando VISIT_CARD_STATUS:
 *   agora                  → visita acontecendo neste momento
 *   pendente               → começou e precisa ser finalizada
 *   aguardando_confirmacao → solicitada, aguardando confirmação
 *   confirmada             → confirmada
 *   agendada               → agendada (ainda não confirmada)
 *   realizada              → realizada com sucesso
 *   nao_compareceu         → finalizada, mas a pessoa não compareceu
 *   nao_sucedida           → ocorreu mas sem resultado
 *   cancelada              → cancelada
 */

import {
  HOUSE_OCCUPANCY,
  HOUSE_OCCUPANCY_LABELS,
  VISITA_STATUS_CLASSES,
  VISITA_STATUS_LABELS,
} from 'dashboard/constants/lais/visita';
import { normalizeQaHouseOccupancy } from 'dashboard/constants/lais/quintoAndarParity';
import {
  getLocalCardStatusFromReference,
  getReferenceScheduledCardStatus,
  mapV3StatusToVisitaStatus,
  REFERENCE_VISIT_STATUS,
} from 'dashboard/routes/dashboard/visitas/utils/referenceVisitLogic';

export const VISITA_CARD_STATUS = Object.freeze({
  NOW: 'agora',
  PENDING: 'pendente',
  WAITING_CONFIRMATION: 'aguardando_confirmacao',
  CONFIRMED: 'confirmada',
  SCHEDULED: 'agendada',
  DONE: 'realizada',
  NOT_HAPPENED: 'nao_compareceu',
  UNSUCCESSFUL: 'nao_sucedida',
  CANCELED: 'cancelada',
  UNAVAILABLE_HOURS: 'horario_indisponivel',
});

export const VISITA_CARD_STATUS_LABELS = Object.freeze({
  [VISITA_CARD_STATUS.NOW]: 'Agora',
  [VISITA_CARD_STATUS.PENDING]: 'Finalização pendente',
  [VISITA_CARD_STATUS.WAITING_CONFIRMATION]: 'Aguardando confirmação',
  [VISITA_CARD_STATUS.CONFIRMED]: 'Visita confirmada',
  [VISITA_CARD_STATUS.SCHEDULED]: 'Visita agendada',
  [VISITA_CARD_STATUS.DONE]: 'Visita realizada',
  [VISITA_CARD_STATUS.NOT_HAPPENED]: 'Visita não realizada',
  [VISITA_CARD_STATUS.UNSUCCESSFUL]: 'Visita não realizada',
  [VISITA_CARD_STATUS.CANCELED]: 'Visita cancelada',
  [VISITA_CARD_STATUS.UNAVAILABLE_HOURS]: 'Horário indisponível',
});

export const VISITA_CARD_STATUS_CLASSES = Object.freeze({
  [VISITA_CARD_STATUS.NOW]: 'text-lais-primary bg-lais-primary-50',
  [VISITA_CARD_STATUS.PENDING]: 'text-amber-700 bg-amber-50',
  [VISITA_CARD_STATUS.WAITING_CONFIRMATION]: 'text-amber-700 bg-amber-50',
  [VISITA_CARD_STATUS.CONFIRMED]: 'text-emerald-700 bg-emerald-50',
  [VISITA_CARD_STATUS.SCHEDULED]: 'text-sky-700 bg-sky-50',
  [VISITA_CARD_STATUS.DONE]: 'text-emerald-700 bg-emerald-50',
  [VISITA_CARD_STATUS.NOT_HAPPENED]: 'text-rose-700 bg-rose-50',
  [VISITA_CARD_STATUS.UNSUCCESSFUL]: 'text-amber-700 bg-amber-50',
  [VISITA_CARD_STATUS.CANCELED]: 'text-rose-700 bg-rose-50',
  [VISITA_CARD_STATUS.UNAVAILABLE_HOURS]: 'text-orange-700 bg-orange-50',
});

const toDate = value => {
  if (!value) return null;
  if (value instanceof Date) return value;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const isSameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

/**
 * Indica se a visita já começou (startTime <= now), espelhando
 * `utils/visits/Time#hasVisitStarted`.
 */
export const hasVisitaStarted = (scheduledAt, now = new Date()) => {
  const start = toDate(scheduledAt);
  const current = toDate(now);
  if (!start || !current) return false;
  return start.getTime() <= current.getTime();
};

const scheduledCardStatus = ({
  scheduledAt,
  status,
  now,
  isVisitConfirmationByAgentEnabled = false,
  isThirdParty = false,
}) => {
  const start = toDate(scheduledAt);
  const current = toDate(now) || new Date();
  const referenceStatus =
    status === 'confirmada'
      ? REFERENCE_VISIT_STATUS.SCHEDULED
      : REFERENCE_VISIT_STATUS.WAITING_CONFIRMATION;
  const referenceCardStatus = getReferenceScheduledCardStatus({
    status: referenceStatus,
    startTime: start || scheduledAt,
    isCurrentDate: start ? isSameDay(start, current) : false,
    now: current,
    isThirdParty,
    isVisitConfirmationByAgentEnabled,
  });

  return getLocalCardStatusFromReference(referenceCardStatus);
};

const structuredStatusValue = (visita, key) => {
  const attrs =
    visita?.additionalAttributes || visita?.additional_attributes || {};
  const structured =
    visita?.structuredStatus || visita?.structured_status || {};
  return (
    structured[key] ||
    structured[key.replace(/[A-Z]/g, char => `_${char.toLowerCase()}`)] ||
    attrs[key] ||
    attrs[key.replace(/[A-Z]/g, char => `_${char.toLowerCase()}`)]
  );
};

const isOwnerUnavailableHours = visita => {
  const reason =
    structuredStatusValue(visita, 'reason') ||
    structuredStatusValue(visita, 'cancellationReason');
  const responsible =
    structuredStatusValue(visita, 'onBehalfOfRole') ||
    structuredStatusValue(visita, 'responsible') ||
    structuredStatusValue(visita, 'cancellationResponsible');

  return (
    ['owner_cannot_this_time', 'person_cannot_attend'].includes(reason) &&
    ['supply', 'owner', 'proprietario'].includes(
      String(responsible || '').toLowerCase()
    )
  );
};

export const mapVisitaStatusFromV3 = status => {
  return mapV3StatusToVisitaStatus(status);
};

export const isVisitaCheckInDone = visitante => {
  const status =
    visitante?.checkInStatus ||
    visitante?.check_in_status ||
    visitante?.checkinStatus ||
    visitante?.checkin_status;

  return ['confirmed', 'confirmado', 'CONFIRMED'].includes(status);
};

/**
 * Deriva o card status de uma visita.
 *
 * @param {Object} visita - { status, scheduled_at|scheduledAt }
 * @param {Date|string} [now] - instante de referência (default: agora)
 * @returns {string|null} um valor de VISITA_CARD_STATUS, ou null se desconhecido
 */
export const getVisitaCardStatus = (visita, now = new Date(), options = {}) => {
  if (!visita || (!visita.status && !visita.status_v3)) return null;

  const status = visita.status_v3
    ? mapVisitaStatusFromV3(visita.status_v3)
    : visita.status;
  const scheduledAt = visita.scheduled_at ?? visita.scheduledAt ?? null;
  const start = toDate(scheduledAt);
  const current = toDate(now) || new Date();
  const attrs =
    visita.additionalAttributes || visita.additional_attributes || {};
  const isVisitConfirmationByAgentEnabled =
    options.isVisitConfirmationByAgentEnabled ??
    visita.isVisitConfirmationByAgentEnabled ??
    visita.is_visit_confirmation_by_agent_enabled ??
    attrs.isVisitConfirmationByAgentEnabled ??
    attrs.is_visit_confirmation_by_agent_enabled ??
    false;
  const isVisitStatusUnavailableHoursEnabled =
    options.isVisitStatusUnavailableHoursEnabled ??
    visita.isVisitStatusUnavailableHoursEnabled ??
    visita.is_visit_status_unavailable_hours_enabled ??
    attrs.isVisitStatusUnavailableHoursEnabled ??
    attrs.is_visit_status_unavailable_hours_enabled ??
    false;
  const isThirdParty =
    options.isThirdParty ??
    visita.isThirdParty ??
    visita.is_third_party ??
    false;

  switch (status) {
    case 'realizada':
      return VISITA_CARD_STATUS.DONE;
    case 'nao_compareceu':
      return VISITA_CARD_STATUS.NOT_HAPPENED;
    case 'nao_sucedida':
      return VISITA_CARD_STATUS.UNSUCCESSFUL;
    case 'paralisada':
      return VISITA_CARD_STATUS.PENDING;
    case 'cancelada':
      if (
        isVisitConfirmationByAgentEnabled &&
        isVisitStatusUnavailableHoursEnabled &&
        isOwnerUnavailableHours(visita)
      ) {
        return VISITA_CARD_STATUS.UNAVAILABLE_HOURS;
      }
      return VISITA_CARD_STATUS.CANCELED;
    case 'solicitada':
    case 'agendada':
    case 'confirmada': {
      // "Agora": começou hoje, dentro da janela padrão de 1h.
      if (
        start &&
        isSameDay(start, current) &&
        hasVisitaStarted(start, current)
      ) {
        const oneHourLater = new Date(start.getTime() + 60 * 60 * 1000);
        if (current.getTime() <= oneHourLater.getTime()) {
          return VISITA_CARD_STATUS.NOW;
        }
      }
      return scheduledCardStatus({
        scheduledAt,
        status,
        now: current,
        isVisitConfirmationByAgentEnabled,
        isThirdParty,
      });
    }
    default:
      return null;
  }
};

export const getVisitaCardStatusLabel = (visita, now = new Date()) => {
  const key = getVisitaCardStatus(visita, now);
  return key ? VISITA_CARD_STATUS_LABELS[key] : null;
};

export const getVisitaStatusDescriptor = status => ({
  status: status || '',
  label: VISITA_STATUS_LABELS[status] || status || 'Não informado',
  className:
    VISITA_STATUS_CLASSES[status] || 'text-lais-text-body bg-lais-bg-soft',
});

export const getVisitaStatusLabel = status =>
  getVisitaStatusDescriptor(status).label;

export const getVisitaStatusClass = status =>
  getVisitaStatusDescriptor(status).className;

const visitStatusValue = visita =>
  visita?.status_v3 ? mapVisitaStatusFromV3(visita.status_v3) : visita?.status;

const additionalValue = (source, camelKey, snakeKey) => {
  const attrs =
    source?.additionalAttributes || source?.additional_attributes || {};
  return attrs[camelKey] ?? attrs[snakeKey];
};

export const isSuccessfulVisit = visita =>
  ['realizada', 'done', 'DONE'].includes(visitStatusValue(visita));

export const isUnsuccessfulVisit = visita =>
  ['nao_compareceu', 'nao_sucedida', 'cancelada'].includes(
    visitStatusValue(visita)
  );

export const buildVisitHistorySummary = (
  visits = [],
  { ownerPresent = false, showVisitHistory = true } = {}
) => {
  const list = Array.isArray(visits) ? visits : [];
  const finished = list.filter(isSuccessfulVisit).length;
  const unfinished = list.filter(isUnsuccessfulVisit).length;
  const hasRisk = unfinished > finished;

  return {
    visible: showVisitHistory && list.length > 1,
    finished,
    unfinished,
    total: list.length,
    icon: hasRisk ? 'i-lucide-circle-alert' : 'i-lucide-circle-check',
    tone: hasRisk
      ? 'text-rose-700 bg-rose-50'
      : 'text-emerald-700 bg-emerald-50',
    title: ownerPresent
      ? 'Histórico com proprietário presente'
      : 'Histórico das últimas visitas',
    description: ownerPresent
      ? 'Use o histórico para alinhar entrada e expectativas com o proprietário.'
      : 'Use o histórico para antecipar risco de não realização nesta visita.',
    finishedLabel: ownerPresent ? 'realizadas com proprietário' : 'realizadas',
    unfinishedLabel: 'não realizadas',
  };
};

const VISIT_HISTORY_PROBLEM_LABELS = Object.freeze({
  lockbox_problems: 'Problemas com a Lockbox',
  no_keys_lockbox: 'A chave não estava na lockbox',
  lockbox_key_unavailable: 'Chaves não estavam disponíveis',
  keys_unavailable: 'Chaves não estavam disponíveis',
  keys_not_available: 'Chaves não estavam disponíveis',
  key_missing: 'Chaves não estavam disponíveis',
  wrong_password: 'Senha de acesso errada',
  invalid_password: 'Senha de acesso errada',
  damaged_keys: 'Chave/fechadura quebrada',
  missplaced_keys: 'Chaves extraviadas',
  owner_no_show: 'Proprietário ou responsável não compareceu.',
  owner_absent: 'Proprietário ou responsável não compareceu.',
  landlord_no_show: 'Proprietário não compareceu',
  tenant_no_show: 'Morador não autorizou a entrada.',
  resident_absent: 'Morador não autorizou a entrada.',
  resident_tenant_didnt_allow_visit: 'Morador não permitiu visita',
  doorman_denied: 'Portaria não autorizou a entrada.',
  access_denied: 'Portaria não autorizou a entrada.',
  no_entrance_permission: 'Sem autorização de entrada',
  no_answer: 'Responsável não respondeu.',
  contact_unavailable: 'Responsável não respondeu.',
  visit_off_allowed_hours: 'Visita fora do horário permitido',
  temporarily_unavailable: 'Imóvel alugado por temporada',
  sold_by_another_company: 'Vendido fora da Imudei',
  rented_by_another_company: 'Alugado fora da Imudei',
  property_not_for_sale: 'Imóvel não era para venda',
  property_not_for_rent: 'Imóvel não era para aluguel',
  owner_quit_announcement: 'Proprietário desistiu de anunciar',
});

const historySources = visit => [
  visit || {},
  visit?.summary || {},
  visit?.additionalAttributes || {},
  visit?.additional_attributes || {},
  visit?.entryAccess || {},
  visit?.entry_access || {},
  visit?.visitFollowup || {},
  visit?.visit_followup || {},
  visit?.postVisitReport || {},
  visit?.post_visit_report || {},
  visit?.postVisitReport?.listingReport || {},
  visit?.post_visit_report?.listing_report || {},
  visit?.imovel || {},
  visit?.contact || {},
  visit?.agent || {},
  visit?.user || {},
];

const historyValue = (visit, ...keys) =>
  historySources(visit)
    .flatMap(source => keys.map(key => source?.[key]))
    .find(value => value !== undefined && value !== null && value !== '');

const historyToken = value =>
  String(value || '')
    .trim()
    .toLowerCase();

const compactHistoryValue = (...values) =>
  values.find(value => value !== undefined && value !== null && value !== '');

const compactHistoryObject = (...values) =>
  values.find(
    value => value && typeof value === 'object' && !Array.isArray(value)
  ) || {};

const keyProblemTokens = new Set([
  'lockbox_problems',
  'no_keys_lockbox',
  'damaged_keys',
  'keys_not_available',
  'lockbox_key_unavailable',
  'wrong_password',
  'invalid_password',
]);

const visitHistoryProblemLabel = visit => {
  const explicit = historyValue(
    visit,
    'accessProblemLabel',
    'access_problem_label',
    'entryAccessProblemLabel',
    'entry_access_problem_label',
    'problemLabel',
    'problem_label',
    'finishReasonLabel',
    'finish_reason_label',
    'cancellationReasonLabel',
    'cancellation_reason_label',
    'reasonLabel',
    'reason_label'
  );
  if (explicit) return explicit;

  const token = historyToken(
    historyValue(
      visit,
      'accessProblem',
      'access_problem',
      'entryAccessProblem',
      'entry_access_problem',
      'problem',
      'finishReason',
      'finish_reason',
      'cancellationReason',
      'cancellation_reason',
      'reason'
    )
  );
  return VISIT_HISTORY_PROBLEM_LABELS[token] || '';
};

const visitHistoryAccessLabel = visit =>
  historyValue(
    visit,
    'accessAuthorizationTypeUtilized',
    'access_authorization_type_utilized',
    'currentAccessAuthorizationType',
    'current_access_authorization_type',
    'accessLabel',
    'access_label',
    'entryAuthorization',
    'entry_authorization'
  );

const visitHistoryDateRawValue = visit =>
  historyValue(visit, 'scheduledAt', 'scheduled_at', 'date', 'visitedAt');

const visitHistoryDateValue = visit =>
  historyValue(visit, 'scheduledAtLabel', 'scheduled_at_label') ||
  visitHistoryDateRawValue(visit);

const visitHistoryDateLabel = visit => {
  const raw = visitHistoryDateValue(visit);
  if (!raw) return 'Data não informada';
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return String(raw);

  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const visitHistoryAddress = visit =>
  historyValue(
    visit,
    'address',
    'endereco',
    'titulo',
    'propertyAddress',
    'property_address'
  ) || 'o imóvel';

const visitHistoryAgentName = visit =>
  historyValue(
    visit,
    'agentName',
    'agent_name',
    'userName',
    'user_name',
    'name',
    'nome'
  ) || 'corretor da visita';

const visitHistoryAgentPhone = visit =>
  historyValue(
    visit,
    'agentPhone',
    'agent_phone',
    'brokerPhone',
    'broker_phone',
    'phoneNumber',
    'phone_number',
    'phone',
    'telefone'
  );

const visitHistoryContactName = visit =>
  historyValue(
    visit,
    'contactName',
    'contact_name',
    'visitorName',
    'visitor_name',
    'clientName',
    'client_name'
  );

const visitHistoryResponsible = visit =>
  compactHistoryValue(
    historyValue(
      visit,
      'responsibleName',
      'responsible_name',
      'keyHolderName',
      'key_holder_name',
      'ownerName',
      'owner_name'
    ),
    historyValue(
      visit,
      'responsiblePhone',
      'responsible_phone',
      'keyHolderPhone',
      'key_holder_phone',
      'ownerPhone',
      'owner_phone'
    )
  );

const visitHistoryEntryObservation = visit =>
  historyValue(
    visit,
    'entryObservation',
    'entry_observation',
    'correctEntryInfo',
    'correct_entry_info',
    'entranceConfirmation',
    'entrance_confirmation',
    'entryInfoConfirmed',
    'entry_info_confirmed'
  );

const visitHistoryComment = visit =>
  historyValue(
    visit,
    'comment',
    'comments',
    'feedback',
    'observacoes',
    'notes',
    'descricao',
    'description'
  );

const visitHistoryKeyStatus = visit =>
  historyValue(
    visit,
    'keyStatus',
    'key_status',
    'keyTypeLabel',
    'key_type_label',
    'keyType',
    'key_type',
    'accessKey',
    'access_key'
  );

const visitEntryAttributes = visit =>
  visit?.additionalAttributes || visit?.additional_attributes || {};

const visitEntryImovelAttributes = visit =>
  visit?.imovel?.additionalAttributes ||
  visit?.imovel?.additional_attributes ||
  {};

const visitEntryAccessSource = visit => {
  const attrs = visitEntryAttributes(visit);
  const imovelAttrs = visitEntryImovelAttributes(visit);

  return compactHistoryObject(
    visit?.entryAccess,
    visit?.entry_access,
    visit?.imovel?.entryAccess,
    visit?.imovel?.entry_access,
    attrs.entryAccess,
    attrs.entry_access,
    imovelAttrs.entryAccess,
    imovelAttrs.entry_access
  );
};

const visitEntryOwnerSource = visit => {
  const attrs = visitEntryAttributes(visit);
  const imovelAttrs = visitEntryImovelAttributes(visit);

  return compactHistoryObject(
    visit?.ownerContact,
    visit?.owner_contact,
    visit?.imovel?.ownerContact,
    visit?.imovel?.owner_contact,
    attrs.ownerContact,
    attrs.owner_contact,
    imovelAttrs.ownerContact,
    imovelAttrs.owner_contact
  );
};

const visitEntryCiqSource = visit => {
  const attrs = visitEntryAttributes(visit);
  const imovelAttrs = visitEntryImovelAttributes(visit);

  return compactHistoryObject(
    visit?.ciqInfo,
    visit?.ciq_info,
    visit?.imovel?.ciqInfo,
    visit?.imovel?.ciq_info,
    attrs.ciqInfo,
    attrs.ciq_info,
    imovelAttrs.ciqInfo,
    imovelAttrs.ciq_info
  );
};

const visitEntryAgentSource = visit =>
  compactHistoryObject(visit?.agent, visit?.user, visit?.broker);

const entryValue = (source, ...keys) =>
  keys
    .map(key => source?.[key])
    .find(value => value !== undefined && value !== null && value !== '');

const visitEntryAccessLabel = (visit, entry) =>
  entryValue(entry, 'accessLabel', 'access_label', 'label') ||
  visitHistoryAccessLabel({ ...visit, entryAccess: entry }) ||
  'Entrada no imóvel';

const visitEntryRows = (visit, entry) =>
  [
    {
      label: 'Entrada no imóvel',
      value: visitEntryAccessLabel(visit, entry),
    },
    {
      label: 'Senha de acesso',
      value: entryValue(
        entry,
        'accessCode',
        'access_code',
        'password',
        'accessPassword',
        'access_password'
      ),
    },
    {
      label: 'Dados do responsável',
      value: [
        entryValue(entry, 'responsibleName', 'responsible_name'),
        entryValue(entry, 'responsiblePhone', 'responsible_phone'),
      ]
        .filter(Boolean)
        .join(' - '),
    },
    {
      label: 'Status das chaves',
      value:
        entryValue(entry, 'keyTypeLabel', 'key_type_label', 'keyStatus') ||
        entryValue(entry, 'accessKey', 'access_key'),
    },
    {
      label: 'Endereço de retirada',
      value: entryValue(entry, 'responsibleAddress', 'responsible_address'),
    },
    {
      label: 'Observações de entrada',
      value: entryValue(
        entry,
        'instructions',
        'accessDetails',
        'access_details',
        'details',
        'helper'
      ),
    },
  ].filter(row => row.value);

const entryContactPhone = contact =>
  entryValue(
    contact,
    'phoneNumber',
    'phone_number',
    'phone',
    'cellphone',
    'contact',
    'telefone'
  );

const normalizedBrazilPhone = phone => {
  const digits = String(phone || '').replace(/\D/g, '');
  if (!digits) return '';
  return digits.startsWith('55') ? digits : `55${digits}`;
};

const buildEntryContactWhatsappUrl = ({ phone, message }) => {
  const normalized = normalizedBrazilPhone(phone);
  if (!normalized) return '';

  return `https://wa.me/${normalized}?text=${encodeURIComponent(message)}`;
};

const entryContact = ({
  key,
  roleLabel,
  actionLabel,
  name,
  phone,
  message,
  tag = '',
}) => {
  if (!name && !phone) return null;

  return {
    key,
    roleLabel,
    actionLabel,
    name: name || 'Contato não informado',
    phone,
    tag,
    whatsappUrl: buildEntryContactWhatsappUrl({ phone, message }),
  };
};

const buildVisitHistoryDetails = ({
  visit,
  address,
  agentName,
  problemLabel,
}) =>
  [
    { label: 'Endereço', value: address === 'o imóvel' ? '' : address },
    { label: 'Cliente', value: visitHistoryContactName(visit) },
    { label: 'Corretor da visita', value: agentName },
    { label: 'Observações', value: visitHistoryComment(visit) },
    {
      label: 'Observações de entrada',
      value: visitHistoryEntryObservation(visit),
    },
    { label: 'Dados do responsável', value: visitHistoryResponsible(visit) },
    { label: 'Status das chaves', value: visitHistoryKeyStatus(visit) },
    { label: 'Problema registrado', value: problemLabel },
  ].filter(row => row.value);

const visitHistoryWhatsAppMessage = item =>
  `Olá! Quero alinhar a visita em ${item.address} do dia ${item.scheduledAtLabel} com ${item.agentName}. Pode me ajudar com o histórico desta visita?`;

const visitHistoryTitle = ({ successful, unsuccessful }) => {
  if (successful) return 'Visita finalizada';
  if (unsuccessful) return 'Visita não realizada';
  return 'Visita registrada';
};

const visitHistoryReferenceTitle = ({ visit, successful, unsuccessful }) => {
  if (successful) {
    return visitHistoryAccessLabel(visit) || 'Visita finalizada';
  }

  const problem = historyToken(
    historyValue(
      visit,
      'problem',
      'entranceProblem',
      'entrance_problem',
      'accessProblem',
      'access_problem',
      'problemReason',
      'problem_reason',
      'reason'
    )
  );

  if (problem === 'missplaced_keys') return 'Informação de entrada errada';
  if (keyProblemTokens.has(problem)) return 'Problemas com a chave do imóvel';

  if (unsuccessful) {
    return visitHistoryAccessLabel(visit) || 'Visita não realizada';
  }

  return visitHistoryTitle({ successful, unsuccessful });
};

const visitHistoryTypeDescriptor = ({ successful, unsuccessful }) => {
  if (successful) return { type: 'FINISHED', typeLabel: 'Finalizada' };
  if (unsuccessful) return { type: 'UNFINISHED', typeLabel: 'Não realizada' };
  return { type: 'REGISTERED', typeLabel: 'Registrada' };
};

const visitHistoryTone = ({ successful, unsuccessful }) => {
  if (successful) return 'text-emerald-700 bg-emerald-50 border-emerald-100';
  if (unsuccessful) return 'text-rose-700 bg-rose-50 border-rose-100';
  return 'text-lais-text-body bg-lais-bg-soft border-lais-border';
};

const visitHistoryIcon = ({ successful, unsuccessful }) => {
  if (successful) return 'i-lucide-circle-check';
  if (unsuccessful) return 'i-lucide-circle-alert';
  return 'i-lucide-clock-3';
};

export const buildVisitHistoryItems = (visits = []) =>
  (Array.isArray(visits) ? visits : [])
    .map((visit, index) => {
      const successful = isSuccessfulVisit(visit);
      const unsuccessful = isUnsuccessfulVisit(visit);
      const { type, typeLabel } = visitHistoryTypeDescriptor({
        successful,
        unsuccessful,
      });
      const problemLabel = visitHistoryProblemLabel(visit);
      const address = visitHistoryAddress(visit);
      const scheduledAtLabel = visitHistoryDateLabel(visit);
      const sortDate = new Date(
        visitHistoryDateRawValue(visit) || visitHistoryDateValue(visit) || 0
      );
      const agentName = visitHistoryAgentName(visit);
      const phone = normalizedBrazilPhone(visitHistoryAgentPhone(visit));
      const title = visitHistoryReferenceTitle({
        visit,
        successful,
        unsuccessful,
      });
      const details = buildVisitHistoryDetails({
        visit,
        address,
        agentName,
        problemLabel,
      });
      const item = {
        id:
          historyValue(visit, 'schedulingId', 'scheduling_id', 'id') ||
          `history-${index}`,
        type,
        typeLabel,
        tone: visitHistoryTone({ successful, unsuccessful }),
        icon: visitHistoryIcon({ successful, unsuccessful }),
        title,
        subtitle:
          problemLabel ||
          visitHistoryComment(visit) ||
          (successful
            ? 'Entrada concluída sem problema registrado.'
            : 'Sem subtítulo de problema informado.'),
        details,
        actions: phone
          ? [
              {
                label: 'Falar com o corretor da visita',
                icon: 'i-lucide-message-circle',
              },
            ]
          : [],
        scheduledAtLabel,
        address,
        agentName,
        phone,
        sortTime: Number.isNaN(sortDate.getTime()) ? 0 : sortDate.getTime(),
        whatsappUrl: '',
      };
      const message = visitHistoryWhatsAppMessage(item);
      return {
        ...item,
        whatsappUrl: phone
          ? `https://wa.me/${phone}?text=${encodeURIComponent(message)}`
          : '',
      };
    })
    .sort((a, b) => b.sortTime - a.sortTime);

export const buildVisitEntryInformation = (
  visit = {},
  { visits = [], initialLimit = 3, companyName = 'Imudei' } = {}
) => {
  const entry = visitEntryAccessSource(visit);
  const owner = visitEntryOwnerSource(visit);
  const ciq = visitEntryCiqSource(visit);
  const agent = visitEntryAgentSource(visit);
  const address = visitHistoryAddress(visit);
  const agentName =
    entryValue(agent, 'name', 'nome') || visitHistoryAgentName(visit);
  const agentPhone =
    entryContactPhone(agent) ||
    historyValue(visit, 'agentPhone', 'agent_phone', 'brokerPhone');
  const ownerName = entryValue(owner, 'name', 'nome');
  const ownerPhone = entryContactPhone(owner);
  const ciqName = entryValue(ciq, 'name', 'nome');
  const ciqPhone = entryContactPhone(ciq);
  const responsibleName = entryValue(
    entry,
    'responsibleName',
    'responsible_name'
  );
  const responsiblePhone = entryValue(
    entry,
    'responsiblePhone',
    'responsible_phone'
  );
  const historyItems = buildVisitHistoryItems(visits);

  const message = role =>
    `Olá! Quero alinhar as informações de entrada do imóvel ${address} com ${role}.`;

  const contacts = [
    entryContact({
      key: 'agent',
      roleLabel: 'Corretor da visita',
      actionLabel: 'Falar com o corretor da visita',
      name: agentName,
      phone: agentPhone,
      message: message(agentName || 'o corretor da visita'),
    }),
    entryContact({
      key: 'ciq',
      roleLabel: 'Consultor Imobiliário',
      actionLabel: 'Falar com o consultor do imóvel',
      name: ciqName,
      phone: ciqPhone,
      tag: ciqName ? 'Imóvel com consultor imobiliário' : '',
      message: message(ciqName || 'o consultor do imóvel'),
    }),
    entryContact({
      key: 'owner',
      roleLabel: 'Proprietário',
      actionLabel: 'Falar com proprietário',
      name: ownerName,
      phone: ownerPhone,
      message: message(ownerName || 'o proprietário'),
    }),
    entryContact({
      key: 'responsible',
      roleLabel: 'Responsável pelas chaves',
      actionLabel: 'Falar com responsável',
      name: responsibleName,
      phone: responsiblePhone,
      message: message(responsibleName || 'o responsável pelas chaves'),
    }),
  ].filter(Boolean);

  return {
    visible: Boolean(visit && (Object.keys(entry).length || contacts.length)),
    title: 'Entrada no imóvel',
    subtitle: visitEntryAccessLabel(visit, entry),
    helper:
      'Confira autorização, responsável, observações e contatos antes de sair para a visita.',
    rows: visitEntryRows(visit, entry),
    contacts,
    historyItems,
    visibleHistoryItems: historyItems.slice(0, initialLimit),
    hasMoreHistory: historyItems.length > initialLimit,
    loadMoreLabel: 'Ver mais',
    referenceComponents: ['VisitEntryInformation', 'VisitHistory', 'LoadMore'],
    companyName,
  };
};

export const resolveHouseOccupancy = source => {
  const imovel = source?.imovel || source;
  const raw =
    imovel?.ocupacao ||
    imovel?.occupancy ||
    additionalValue(source, 'houseOccupancy', 'house_occupancy') ||
    additionalValue(imovel, 'houseOccupancy', 'house_occupancy');
  return normalizeQaHouseOccupancy(raw) || HOUSE_OCCUPANCY.EMPTY;
};

export const occupiedHouseNoticeDescriptor = source => {
  const occupancy = resolveHouseOccupancy(source);
  const hasResident = occupancy !== HOUSE_OCCUPANCY.EMPTY;
  const isTenantLiving = occupancy === HOUSE_OCCUPANCY.TENANT;

  return {
    occupancy,
    visible: hasResident,
    isTenantLiving,
    title: HOUSE_OCCUPANCY_LABELS[occupancy] || 'Imóvel ocupado',
    description: isTenantLiving
      ? 'Confirme a visita com o morador antes do horário para evitar desencontro.'
      : 'Combine a entrada com a pessoa responsável antes de confirmar a visita.',
    icon: isTenantLiving ? 'i-lucide-message-circle' : 'i-lucide-home',
  };
};

export const buildTalkToTenantMessage = ({
  tenant,
  agentName = 'Imudei',
  imovel,
  visit,
} = {}) => {
  const tenantName = tenant?.name || tenant?.nome || 'morador';
  const address =
    imovel?.titulo ||
    imovel?.endereco ||
    visit?.imovel?.titulo ||
    visit?.imovel?.endereco ||
    'o imóvel';
  const scheduledAt = visit?.scheduledAt || visit?.scheduled_at;
  const scheduledDate = scheduledAt ? new Date(scheduledAt) : null;
  const dateText =
    scheduledDate && !Number.isNaN(scheduledDate.getTime())
      ? new Intl.DateTimeFormat('pt-BR', {
          dateStyle: 'short',
          timeStyle: 'short',
        }).format(scheduledDate)
      : 'o horário combinado';

  return `Olá, ${tenantName}! Aqui é ${agentName}. Preciso confirmar a visita em ${address} para ${dateText}. Pode me ajudar com a entrada?`;
};

export default getVisitaCardStatus;
